import { useTranslation } from "react-i18next";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { type WorkflowData } from "../../types";
import { adaptWorkflowSummary } from "./adapter";
import { WorkflowCard } from "./WorkflowCard";

const FULL_WIDTH_GROUP_ID = "agro_advisory";

interface WorkflowSummaryProps {
  data: WorkflowData;
}

export function WorkflowSummary({ data }: WorkflowSummaryProps) {
  const { t } = useTranslation();
  const groups = adaptWorkflowSummary(data, t);
  const taskGroups = groups.filter(group => group.id !== FULL_WIDTH_GROUP_ID);
  const fullWidthGroup = groups.find(group => group.id === FULL_WIDTH_GROUP_ID);

  return (
    <div className="flex flex-col gap-6">
      <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {taskGroups.map(group => (
          <WorkflowCard key={group.id} {...group} />
        ))}
      </section>
      {fullWidthGroup ? (
        <section>
          <WorkflowCard {...fullWidthGroup} orientation="horizontal" />
        </section>
      ) : null}
    </div>
  );
}

function WorkflowCardSkeleton({ rows }: { rows: number }) {
  return (
    <Card className="h-full gap-0 border-none p-0">
      <CardHeader className="gap-0 rounded-t-xl bg-blue-50 px-4 py-3">
        <Skeleton className="mx-auto h-5 w-32" />
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-4 py-4">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex items-center gap-3">
            <Skeleton className="size-5 shrink-0 rounded-full" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-10 shrink-0" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function WorkflowSummarySkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <WorkflowCardSkeleton key={index} rows={3} />
        ))}
      </section>
      <section>
        <WorkflowCardSkeleton rows={2} />
      </section>
    </div>
  );
}
